import { motion, AnimatePresence } from 'framer-motion';
import { Table } from 'ka-table';
import { DataType, SortingMode } from 'ka-table/enums';
import { useNavigate } from 'react-router-dom';
import styles from './Complaints.module.css';
import { formatDate, STATUS_BADGE_CLASS } from '../complaintsHandler';

// ── Animation Variants ────────────────────────────────────────────────────
const tableVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.18 } },
  exit: { opacity: 0, transition: { duration: 0.18 } },
};

const emptyVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.25 } },
};

// ── Cell Sub-components ───────────────────────────────────────────────────

function StatusCell({ value, STATUS_COLORS }) {
  const cls = STATUS_BADGE_CLASS[value] || 'badgeOpen';
  const color = STATUS_COLORS[value] ?? 'var(--info)';
  return (
    <span
      className={`${styles.badge} ${styles[cls]}`}
      style={{ borderColor: color, color: color }}
    >
      {value}
    </span>
  );
}

function TicketCell({ value }) {
  return <strong style={{ color: 'var(--accent)' }}>{value}</strong>;
}

function TruncateCell({ value }) {
  return <span className={styles.truncate}>{value}</span>;
}


// ── Main Export ───────────────────────────────────────────────────────────
export default function ComplaintsTable({ rows, search, statusFilter, STATUS_COLORS }) {
  const navigate = useNavigate();

  const firstRaw = rows[0];
  const columns = [
    { key: 'ticketNumber', title: firstRaw?.Name?.label ?? 'Ticket #', dataType: DataType.String, width: 110 },
    { key: 'dateReceived', title: firstRaw?.['PIRM_Base__Date_Recieved__c']?.label ?? 'Date Received', dataType: DataType.String, width: 130 },
    { key: 'company', title: firstRaw?.['PIRM_Base__Company__c']?.label ?? 'Company', dataType: DataType.String, width: 160 },
    { key: 'product', title: firstRaw?.['PIRM_Base__Product__c']?.label ?? 'Product', dataType: DataType.String, width: 140 },
    { key: 'issue', title: firstRaw?.['PIRM_Base__Issue__c']?.label ?? 'Issue', dataType: DataType.String, width: 200 },
    { key: 'status', title: firstRaw?.['PIRM_Base__Status__c']?.label ?? 'Status', dataType: DataType.String, width: 140 },
    { key: 'owner', title: firstRaw?.['Owner.Name']?.label ?? 'Owner', dataType: DataType.String, width: 120 },
    { key: 'primarySource', title: firstRaw?.['PIRM_Base__Primary_Source__c']?.label ?? 'Primary Source', dataType: DataType.String, width: 130 },
    { key: 'chevron', title: '', dataType: DataType.String, width: 40 },
  ];

  function flattenRow(raw) {
    return {
      id: raw.Id?.value ?? '',
      ticketNumber: raw.Name?.value ?? '',
      dateReceived: raw['PIRM_Base__Date_Recieved__c']?.value ?? '',
      company: raw['PIRM_Base__Company__c']?.value ?? '',
      product: raw['PIRM_Base__Product__c']?.value ?? '',
      issue: raw['PIRM_Base__Issue__c']?.value ?? '',
      status: raw['PIRM_Base__Status__c']?.value ?? '',
      owner: raw['Owner.Name']?.value ?? '',
      primarySource: raw['PIRM_Base__Primary_Source__c']?.value ?? '',
    };
  }
  const flatRows = rows.map(flattenRow);


  return (
    <motion.div className={styles.tableWrap}>
      <AnimatePresence mode="wait">
        <motion.div
          key={`${search}-${statusFilter}`}
          variants={tableVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <Table
            columns={columns}
            data={flatRows}
            rowKeyField="id"
            sortingMode={SortingMode.Single}
            paging={{
              enabled: true,
              pageIndex: 0,
              pageSize: 10,
              pageSizes: [10, 20, 30],
            }}
            childComponents={{
              cellText: {
                content: (props) => {
                  if (props.column.key === 'ticketNumber') return <TicketCell value={props.value} />;
                  if (props.column.key === 'status') return <StatusCell value={props.value} STATUS_COLORS={STATUS_COLORS} />;
                  if (props.column.key === 'issue') return <TruncateCell value={props.value} />;
                  if (props.column.key === 'dateReceived') return <span>{formatDate(props.value)}</span>;
                  if (props.column.key === 'chevron') return <i className="fa-solid fa-chevron-right u-row-chevron" aria-hidden="true" />;
                  return undefined;
                },
              },
              dataRow: {
                elementAttributes: (props) => ({
                  onClick: () => navigate(`/complaints/${props.rowData.id}`),
                  className: `${styles.tableRow} u-row-link`,
                  title: `Open ${props.rowData.ticketNumber}`,
                }),
              },
              headCell: {
                elementAttributes: () => ({ className: styles.headCell }),
              },
              cell: {
                elementAttributes: () => ({ className: styles.cell }),
              },
              paging: {
                elementAttributes: () => ({
                  className: 'custom-paging'
                })
              },
              pagingPages: {
                elementAttributes: () => ({
                  className: 'd-flex justify-content-end'
                })
              },
              pagingIndex: {
                elementAttributes: props => ({
                  className: `pagination-item ${props.isActive ? 'active' : ''}`
                })
              },
              pagingSizes: {
                content: ({ dispatch, pageSize }) => (
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted">Rows per page:</span>
                    <input
                      type="number"
                      value={pageSize}
                      min={1}
                      onChange={(e) => {
                        const size = Number(e.target.value) || 1;
                        dispatch({ type: 'UpdatePageSize', pageSize: size });
                      }}
                      style={{ width: '60px', textAlign: 'center' }}
                    />
                  </div>
                )
              }
            }}
          />
        </motion.div>
      </AnimatePresence>

      {/* Empty State */}
      <AnimatePresence>
        {rows.length === 0 && (
          <motion.div
            className={styles.emptyState}
            variants={emptyVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              width={40}
              height={40}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <p>No complaints match your filters.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}